import { Box } from "@mui/material";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FilterChipDef, SearchFilters } from "./filterChipDefs";
import { useFilterDisplayLabel } from "../../hooks/useFilterDisplayLabel";

interface SearchBarTokenProps {
  readonly chipDef: FilterChipDef;
  readonly filters: SearchFilters;
  readonly customerId?: string;
  readonly siteMap?: Map<string, string>;
  readonly onEdit: (chipDef: FilterChipDef) => void;
  readonly onRemove: (chipDef: FilterChipDef) => void;
}

export default function SearchBarToken({
  chipDef,
  filters,
  customerId,
  siteMap,
  onEdit,
  onRemove,
}: SearchBarTokenProps) {
  const { t } = useTranslation("search");
  const getDisplayLabel = useFilterDisplayLabel(customerId);
  const displayLabel = getDisplayLabel(chipDef);
  const displayValue = chipDef.getDisplayValue(filters, siteMap);

  const handleBodyClick = () => onEdit(chipDef);
  const handleRemoveClick = (e: MouseEvent) => {
    e.stopPropagation();
    onRemove(chipDef);
  };

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        px: "8px",
        py: "3px",
        fontSize: "0.75rem",
        fontFamily: "inherit",
        color: "var(--text-primary)",
        backgroundColor: "var(--accent-dim)",
        border: "1px solid var(--accent)",
        borderRadius: "8px",
        whiteSpace: "nowrap",
        flexShrink: 0,
        "&:hover": {
          backgroundColor: "var(--bg-hover)",
        },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={handleBodyClick}
        aria-label={t("searchBar.editToken", {
          displayLabel,
          displayValue,
          defaultValue: `${displayLabel}: ${displayValue}, click to edit`,
        })}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          background: "transparent",
          border: "none",
          padding: 0,
          color: "inherit",
          font: "inherit",
          cursor: "pointer",
        }}
      >
        <Box component="span" sx={{ color: "var(--accent)", fontWeight: 600 }}>
          {displayLabel}:
        </Box>
        <Box component="span">{displayValue}</Box>
      </Box>
      <Box
        component="button"
        type="button"
        aria-label={t("searchBar.removeFilter", {
          displayLabel,
          defaultValue: `Remove ${displayLabel} filter`,
        })}
        onClick={handleRemoveClick}
        sx={{
          ml: "2px",
          px: "2px",
          cursor: "pointer",
          color: "rgba(184, 208, 255, 0.6)",
          background: "transparent",
          border: "none",
          "&:hover": { color: "var(--text-primary)" },
        }}
      >
        ×
      </Box>
    </Box>
  );
}
